import React, { useContext, useState, useMemo, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, registerDefaultsForModule } from '@glu/theming';
import { CaretDownNextIcon } from '@glu/icons-react';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

/**
 * Context below is used for connecting Accordion component to AccordionPanels,
 * provides them openPanelIdsArr array that contains panelsIds of open panels.
 */

var AccordionContext = /*#__PURE__*/React.createContext({});

/**
 * Context below is responsible for connecting AccordionPanel component
 * to AccordionPanelHeader and AccordionPanelContent components, sharing props with them.
 * @props isOpen { Boolean } - Determine if AccordionPanel is expanded or collapsed.
 * @props handleSetCurrentOpenPanelsIdsArr { function } - onClick callback panel header is
 *        responsible for managing open accordion state.
 * @props panelId { string } - AccordionPanel's identifier should be unique across siblings.
 */

var AccordionPanelContext = /*#__PURE__*/React.createContext({});

var useStyles = createUseStyles(function (_ref) {
  var accordion = _ref.accordion;
  return {
    parentSection: _objectSpread2({}, accordion.parentSection),
    nestedSection: _objectSpread2({}, accordion.nestedSection)
  };
});

var uniqueId = 0;

var AccordionPanel = function AccordionPanel(_ref) {
  var panelId = _ref.panelId,
      children = _ref.children,
      isOpen = _ref.isOpen,
      className = _ref.className,
      dataQa = _ref.dataQa,
      isNested = _ref.isNested,
      rest = _objectWithoutProperties(_ref, ["panelId", "children", "isOpen", "className", "dataQa", "isNested"]);

  var _useContext = useContext(AccordionContext),
      openPanelIdsArr = _useContext.openPanelIdsArr,
      handleSetCurrentOpenPanelsIdsArr = _useContext.handleSetCurrentOpenPanelsIdsArr;

  var classes = useStyles(rest); // In case of AccordionPanel exists out of Accordion scope local state will be responsible
  // for managing Accordion Panel Toggle functionality.

  var panelIsPartOfAccordion = openPanelIdsArr !== undefined;

  var _useState = useState(isOpen),
      _useState2 = _slicedToArray(_useState, 2),
      isPanelOpenLocally = _useState2[0],
      setIsPaleOpenLocally = _useState2[1];

  var managePanelOpenStateLocally = function managePanelOpenStateLocally() {
    setIsPaleOpenLocally(function (prevOpenState) {
      return !prevOpenState;
    });
  };

  var dataQAFallback = useMemo(function () {
    return "accordion-".concat(uniqueId++);
  }, []); // eslint-disable-line no-plusplus
  // Hook useLayoutEffect is called once before AccordionPanel has been rendered to update
  // accordion's openPanelIdsArr in case of `isOpen` prop has been passed to AccordionPanel.

  useLayoutEffect(function () {
    if (isOpen && panelIsPartOfAccordion) {
      handleSetCurrentOpenPanelsIdsArr(panelId);
    }
  }, [handleSetCurrentOpenPanelsIdsArr, isOpen, panelId, panelIsPartOfAccordion]);
  return /*#__PURE__*/React.createElement(AccordionPanelContext.Provider, {
    value: {
      isOpen: panelIsPartOfAccordion ? openPanelIdsArr.indexOf(panelId) !== -1 : isPanelOpenLocally,
      handleSetCurrentOpenPanelsIdsArr: panelIsPartOfAccordion ? handleSetCurrentOpenPanelsIdsArr : managePanelOpenStateLocally,
      panelId: panelId
    }
  }, /*#__PURE__*/React.createElement("section", {
    "data-qa": dataQa || dataQAFallback,
    className: "".concat(isNested === true ? classes.nestedSection : classes.parentSection, " ").concat(className)
  }, children));
};

AccordionPanel.propTypes = {
  isNested: PropTypes.bool,
  panelId: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element, PropTypes.node, PropTypes.string]),
  isOpen: PropTypes.bool,
  dataQa: PropTypes.string
};
AccordionPanel.defaultProps = {
  isNested: false,
  className: '',
  children: null,
  isOpen: false,
  dataQa: ''
};
AccordionPanel.displayName = 'AccordionPanel';

/* istanbul ignore file */
var legacy = (function (_ref) {
  var palette = _ref.palette,
      typography = _ref.typography;
  return {
    header: {
      padding: '1px 20px',
      cursor: 'pointer',
      color: palette.text.dark,
      lineHeight: '30px',
      fontFamily: typography.text.fontFamily,
      fontSize: 16,
      fontWeight: typography.text.fontWeight,
      outlineStyle: "".concat(palette.focus.normal, " solid 1px")
    },
    content: {
      lineHeight: '24px',
      color: palette.grey.G600,
      size: 14,
      fontFamily: typography.text.fontFamily,
      fontWeight: 400
    },
    icon: {
      display: 'none'
    },
    parentSection: {
      '& h3': {
        backgroundColor: '#c8dffc',
        margin: '0 0 10px 0'
      }
    },
    nestedSection: {
      '& h3': {
        backgroundColor: palette.primary.light,
        paddingLeft: 35,
        margin: '0 0 10px 0'
      },
      '& article': {
        padding: '0 0 10px 56px'
      }
    }
  };
});

/* istanbul ignore file */
var ash = (function (_ref) {
  var palette = _ref.palette,
      typography = _ref.typography;
  return {
    header: {
      padding: '1px 8px',
      cursor: 'pointer',
      color: palette.text.dark,
      lineHeight: '30px',
      fontFamily: typography.text.fontFamily,
      fontSize: 16,
      fontWeight: typography.text.fontWeight,
      outlineStyle: "".concat(palette.focus.normal, " solid 1px")
    },
    content: {
      lineHeight: '24px',
      color: palette.grey.G600,
      size: 14,
      fontFamily: typography.text.fontFamily,
      fontWeight: 400
    },
    icon: {
      display: 'flex',
      arrowSize: 16,
      arrowFill: '#4c4c4c',
      cursor: 'pointer',
      transitionDuration: '.3s',
      transitionTimingFunction: 'ease-in-out',
      transitionDelay: '0s'
    },
    parentSection: {
      '& h3': {
        margin: 0
      }
    },
    nestedSection: {
      padding: '1px 6px 1px 27px',
      '& h3': {
        margin: 0
      }
    }
  };
});

/* istanbul ignore file */
var themes = {
  ash: ash,
  legacy: legacy
};
var defaultsRegistered = false;
function registerAccordionDefaults() {
  if (!defaultsRegistered) {
    registerDefaultsForModule('accordion', function (theme) {
      return themes[theme.name](theme);
    });
    defaultsRegistered = true;
  }
}

registerAccordionDefaults();
var useStyles$1 = createUseStyles(function (_ref) {
  var _ref$accordion$header = _ref.accordion.header,
      padding = _ref$accordion$header.padding,
      cursor = _ref$accordion$header.cursor,
      lineHeight = _ref$accordion$header.lineHeight,
      color = _ref$accordion$header.color,
      fontFamily = _ref$accordion$header.fontFamily,
      fontSize = _ref$accordion$header.fontSize,
      fontWeight = _ref$accordion$header.fontWeight,
      outlineStyle = _ref$accordion$header.outlineStyle;
  return {
    accordionHeader: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      fontFamily: fontFamily,
      fontWeight: fontWeight,
      fontSize: fontSize,
      lineHeight: lineHeight,
      color: color,
      cursor: cursor,
      flexDirection: function flexDirection(_ref2) {
        var iconRight = _ref2.iconRight;
        return iconRight ? 'row-reverse' : undefined;
      },
      justifyContent: function justifyContent(_ref3) {
        var iconRight = _ref3.iconRight;
        return iconRight ? 'space-between' : undefined;
      },
      width: function width(_ref4) {
        var iconRight = _ref4.iconRight;
        return iconRight ? '100%' : undefined;
      },
      border: 'none',
      backgroundColor: 'transparent',
      padding: '6px 0',
      '&:focus': {
        outline: 'none',
        '& $accordionHeaderContent': {
          outline: outlineStyle
        }
      }
    },
    accordionHeaderContent: {
      padding: padding
    }
  };
});

registerAccordionDefaults();
var useStyles$2 = createUseStyles(function (_ref) {
  var _ref$accordion$icon = _ref.accordion.icon,
      arrowSize = _ref$accordion$icon.arrowSize,
      arrowFill = _ref$accordion$icon.arrowFill,
      transitionDuration = _ref$accordion$icon.transitionDuration,
      transitionTimingFunction = _ref$accordion$icon.transitionTimingFunction,
      transitionDelay = _ref$accordion$icon.transitionDelay,
      display = _ref$accordion$icon.display;
  return {
    icon: {
      height: 20,
      width: 20,
      justifyContent: 'center',
      alignItems: 'center',
      transform: 'rotate(-90deg)',
      transition: "transform ".concat(transitionDuration, " ").concat(transitionTimingFunction),
      'transition-delay': transitionDelay,
      '& svg': {
        width: arrowSize,
        height: arrowSize,
        fill: arrowFill
      },
      display: display
    },
    iconRotated: {
      transform: 'rotate(0deg)'
    }
  };
});

var AccordionPanelHeaderIcon = function AccordionPanelHeaderIcon(_ref) {
  var title = _ref.title,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["title", "className"]);

  var _useContext = useContext(AccordionPanelContext),
      isOpen = _useContext.isOpen;

  var classes = useStyles$2(rest);
  return /*#__PURE__*/React.createElement(CaretDownNextIcon, _extends({
    className: "".concat(classes.icon, " ").concat(isOpen ? classes.iconRotated : '', " ").concat(className),
    focusable: false,
    title: title
  }, rest));
};

AccordionPanelHeaderIcon.propTypes = {
  title: PropTypes.string,
  className: PropTypes.string
};
AccordionPanelHeaderIcon.defaultProps = {
  className: '',
  title: undefined
};

var AccordionPanelHeaderComponent = function AccordionPanelHeaderComponent(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["children", "className"]);

  var _useContext = useContext(AccordionPanelContext),
      isOpen = _useContext.isOpen,
      panelId = _useContext.panelId,
      handleSetCurrentOpenPanelsIdsArr = _useContext.handleSetCurrentOpenPanelsIdsArr;

  var classes = useStyles$1(rest);
  return /*#__PURE__*/React.createElement("h3", {
    className: className
  }, /*#__PURE__*/React.createElement("button", {
    "aria-expanded": isOpen,
    "aria-controls": panelId,
    "aria-disabled": "false",
    type: "button",
    className: classes.accordionHeader,
    onClick: function onClick() {
      return handleSetCurrentOpenPanelsIdsArr(panelId);
    }
  }, /*#__PURE__*/React.createElement(AccordionPanelHeaderIcon, null), /*#__PURE__*/React.createElement("div", {
    className: classes.accordionHeaderContent
  }, typeof children === 'string' ? /*#__PURE__*/React.createElement("span", null, children) : children)));
};

AccordionPanelHeaderComponent.propTypes = {
  /** class name on the H3 tag wrapping the header */
  className: PropTypes.string,

  /** content of the accordion header */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.string]),

  /** controls whether the icon is displayed on the right or left of the label */
  // eslint-disable-next-line react/no-unused-prop-types
  iconRight: PropTypes.bool
};
AccordionPanelHeaderComponent.defaultProps = {
  className: '',
  children: null,
  iconRight: false
};
AccordionPanelHeaderComponent.displayName = 'AccordionPanelHeader';

var useStyles$3 = createUseStyles(function (_ref) {
  var _ref$accordion$conten = _ref.accordion.content,
      lineHeight = _ref$accordion$conten.lineHeight,
      color = _ref$accordion$conten.color,
      size = _ref$accordion$conten.size,
      fontFamily = _ref$accordion$conten.fontFamily,
      fontWeight = _ref$accordion$conten.fontWeight;
  return {
    accordionContent: {
      fontFamily: fontFamily,
      fontSize: size,
      fontWeight: fontWeight,
      lineHeight: lineHeight,
      color: color,
      overflow: 'hidden',
      margin: [0, 0, 0, 28]
    }
  };
});

registerAccordionDefaults();

var AccordionPanelContentComponent = function AccordionPanelContentComponent(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["children", "className"]);

  var _useContext = useContext(AccordionPanelContext),
      isOpen = _useContext.isOpen,
      panelId = _useContext.panelId;

  var classes = useStyles$3(rest);
  return isOpen && /*#__PURE__*/React.createElement("article", {
    className: "".concat(classes.accordionContent, " ").concat(className),
    role: "region",
    "aria-labelledby": panelId
  }, typeof children === 'string' ? /*#__PURE__*/React.createElement("span", null, children) : children);
};

AccordionPanelContentComponent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array, PropTypes.node, PropTypes.string])
};
AccordionPanelContentComponent.defaultProps = {
  className: '',
  children: null
};
AccordionPanelContentComponent.displayName = 'AccordionPanelContent';

registerAccordionDefaults();

var Accordion = function Accordion(_ref) {
  var children = _ref.children,
      allowMultiplePanelsOpen = _ref.allowMultiplePanelsOpen,
      className = _ref.className;

  /**
   * State openPanelIdsArr manages open state of accordion component.
   * @param {array} openPanelIdsArr - Current open panels' IDs array.
   * @param {function} updateOpenPanelIdState - Update open Panels' Ids array.
   */
  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      openPanelIdsArr = _useState2[0],
      updateOpenPanelIdState = _useState2[1];

  var handleSetCurrentOpenPanelsIdsArr = useCallback(function (managedPanelId) {
    updateOpenPanelIdState(function (prevOpenStateArr) {
      var managedPanelIdIsOpen = prevOpenStateArr.indexOf(managedPanelId) !== -1; // If it's not allowed multiple panels to be open and current managed panel is open,
      // no panel header should be open otherwise managed panel id should be open.

      if (!allowMultiplePanelsOpen) {
        return managedPanelIdIsOpen ? [] : [managedPanelId];
      } // If current managed panel is already open, so openPanelIdsArr state array
      // contains it's panelId, it's id needed to be removed from state array,
      // otherwise this panelId should be added to openPanelIdsArr state array.


      return managedPanelIdIsOpen ? prevOpenStateArr.filter(function (item) {
        return item !== managedPanelId;
      }) : prevOpenStateArr.concat([managedPanelId]);
    });
  }, [allowMultiplePanelsOpen]);
  return /*#__PURE__*/React.createElement(AccordionContext.Provider, {
    value: {
      openPanelIdsArr: openPanelIdsArr,
      handleSetCurrentOpenPanelsIdsArr: handleSetCurrentOpenPanelsIdsArr
    }
  }, /*#__PURE__*/React.createElement("div", {
    "data-allow-toggle": true,
    "data-allow-multiple": allowMultiplePanelsOpen,
    className: className
  }, children));
};

Accordion.propTypes = {
  className: PropTypes.string,
  allowMultiplePanelsOpen: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array, PropTypes.element, PropTypes.string])
};
Accordion.defaultProps = {
  className: '',
  allowMultiplePanelsOpen: true,
  children: null
}; // Bind public components to Accordion so Panel, Header and Content components
// could be used while Accordion has been imported.

Accordion.Panel = AccordionPanel;
Accordion.Header = AccordionPanelHeaderComponent;
Accordion.Content = AccordionPanelContentComponent;
Accordion.displayName = 'Accordion';

/* istanbul ignore file */

export default Accordion;
export { AccordionContext, AccordionPanelContext, AccordionPanelHeaderIcon as AccordionIcon, AccordionPanel, AccordionPanelHeaderComponent as AccordionPanelHeader, AccordionPanelContentComponent as AccordionPanelContent };
